// =============================================================================
// PREVIOUS IMPLEMENTATION — kept verbatim below for reference, intentionally
// inert. Panels used to own their own state, which meant leaving a step threw
// away whatever had been done on it and left the rail unable to describe it.
// The reference holds all of it in the page, so the state moved up.
// =============================================================================
//
// import { OnboardingStep } from "../onboardingSteps";
//
// export type DmsScenario = "contract+attestation" | "contract-only";
//
// export interface PanelProps {
//   /** The flow's steps, so a panel can talk about what comes next. */
//   steps: OnboardingStep[];
//   /** Absent on the first step. */
//   onBack?: () => void;
//   /** Absent on the last step. */
//   onContinue?: () => void;
//   /** Step 3 only: the document set, and the preview switch that changes it. */
//   scenario?: DmsScenario;
//   onScenarioChange?: (scenario: DmsScenario) => void;
// }

// ==== ACTIVE IMPLEMENTATION ==================================================

import { OnboardingStep, StepId } from "../onboardingSteps";

/**
 * Which document set the DMS attached to this reservation. A mobility lease
 * needs the eligibility attestation alongside the contract; a standard one does
 * not. Step 3 exposes it as a preview switch so both can be reviewed — the
 * tenant never chooses it.
 */
export type DmsScenario = "contract+attestation" | "contract-only";

/** The two things a tenant signs, and what the signature screen is opened for. */
export type SignatureTarget = "documents" | "inventory";

/**
 * Everything the tenant has done so far. Held by the page rather than by the
 * panels: the rail describes this state ("Both documents signed", "Certificate
 * received"), and a panel that owned it would lose it the moment the tenant
 * looked at another step.
 */
export interface OnboardingState {
  scenario: DmsScenario;
  /**
   * The chosen files themselves, not just their names: DocumentDropzone already
   * validates and hands back a File, and the upload will need the body.
   */
  identityFile: File | null;
  /**
   * The name of the identity document already on file, from an earlier visit or
   * from this session's upload. The File above lives only while the tenant is
   * choosing one, so on its own it makes a returning tenant look as though they
   * had sent nothing. Mirrors `insuranceSent` for step 6.
   */
  identityFileName: string;
  documentsSigned: boolean;
  /** PNG data URL of the drawn signature, for the preview thumbnail. */
  documentSignature: string;
  inventorySigned: boolean;
  inventorySignature: string;
  insuranceFile: File | null;
  /**
   * Whether a certificate is already on file from an earlier visit. The File
   * above only exists while the tenant is choosing one, so on its own it makes
   * a returning tenant look as though they had sent nothing.
   */
  insuranceSent: boolean;
  /**
   * The tenant's inventory photos themselves, not just how many. A count was
   * enough while the step was a mock-up; submitting them needs the bodies.
   */
  tenantPhotos: File[];
  /** What the tenant wants on record about the property's condition. */
  inventoryComments: string;
  /** URLs of photos already submitted, so a returning tenant sees their own. */
  inventoryPhotoUrls: string[];
}

export const INITIAL_ONBOARDING_STATE: OnboardingState = {
  scenario: "contract+attestation",
  identityFile: null,
  identityFileName: "",
  documentsSigned: false,
  documentSignature: "",
  inventorySigned: false,
  inventorySignature: "",
  insuranceFile: null,
  insuranceSent: false,
  tenantPhotos: [],
  inventoryComments: "",
  inventoryPhotoUrls: [],
};

/**
 * Every centre panel takes the same props so the workspace can swap them by
 * step id without a special case. A panel renders its own heading, body and
 * footer — the shell only provides the column.
 */
export interface PanelProps {
  /** The flow's steps, so a panel can talk about what comes next. */
  steps: OnboardingStep[];
  state: OnboardingState;
  /** Merges a patch into the shared state above. */
  update: (patch: Partial<OnboardingState>) => void;
  /** Jump to any step in the flow. */
  goTo: (id: StepId) => void;
  /** Hand over to the signature screen for one of the signable documents. */
  startSigning: (target: SignatureTarget) => void;
  /** Absent on the first step. */
  onBack?: () => void;
  /** Absent on the last step. */
  onContinue?: () => void;
}
